/** Employee engagement — module 18. */

export type SurveyKind = 'ENGAGEMENT' | 'PULSE' | 'ENPS' | 'CUSTOM'
export type SurveyAnonymity = 'ANONYMOUS' | 'IDENTIFIED'
export type SurveyStatus = 'DRAFT' | 'OPEN' | 'CLOSED' | 'CANCELLED'
export type QuestionType = 'SCALE_1_5' | 'SCALE_0_10' | 'YES_NO' | 'SINGLE_CHOICE' | 'TEXT'
export type AnnouncementStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
export type RecognitionVisibility = 'PUBLIC' | 'MANAGER_ONLY' | 'PRIVATE'

export const SURVEY_KIND_LABELS: Record<SurveyKind, string> = {
  ENGAGEMENT: 'Engagement survey',
  PULSE: 'Pulse',
  ENPS: 'eNPS',
  CUSTOM: 'Something else',
}

export const SURVEY_STATUS_LABELS: Record<SurveyStatus, string> = {
  DRAFT: 'Not sent',
  OPEN: 'Collecting',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
}

export const ANONYMITY_LABELS: Record<SurveyAnonymity, string> = {
  ANONYMOUS: 'Anonymous',
  IDENTIFIED: 'Named',
}

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  SCALE_1_5: '1 to 5',
  SCALE_0_10: '0 to 10',
  YES_NO: 'Yes or no',
  SINGLE_CHOICE: 'One of several',
  TEXT: 'Free text',
}

export const ANNOUNCEMENT_STATUS_LABELS: Record<AnnouncementStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
}

export interface SurveyView {
  id: string
  legalEntityId: string | null
  code: string
  title: string
  description: string | null
  kind: string
  anonymity: string
  audienceKind: string
  audienceRef: string | null
  /** Null until somebody sets it; publishing without one is refused. */
  minGroupSize: number | null
  opensOn: string | null
  closesOn: string | null
  status: string
  publishedAt: string | null
  closedAt: string | null
  questionCount: number
  invited: number
  responded: number
  anonymityNote: string
}

export interface SurveyQuestionView {
  id: string
  surveyId: string
  questionOrder: number
  text: string
  questionType: string
  options: string[]
  required: boolean
}

export interface QuestionResultView {
  questionId: string
  text: string
  questionType: string
  responses: number
  /** Null when the group is too small — see withheldReason. */
  average: number | null
  distribution: Record<string, number>
  withheldReason: string | null
}

export interface EnpsView {
  score: number | null
  promoters: number
  passives: number
  detractors: number
  responses: number
  note: string
}

export interface GroupResultView {
  departmentId: string
  responses: number
  average: number | null
  withheldReason: string | null
}

export interface SurveyResultView {
  surveyId: string
  title: string
  anonymity: string
  invited: number
  responded: number
  minGroupSize: number
  released: boolean
  questions: QuestionResultView[]
  enps: EnpsView | null
  byDepartment: GroupResultView[]
  note: string
}

export interface RecognitionView {
  id: string
  fromEmployeeId: string
  fromName: string | null
  toEmployeeId: string
  toName: string | null
  valueTag: string | null
  message: string
  points: number | null
  visibility: string
  createdAt: string | null
  pointsNote: string
}

export interface EngagementSettingView {
  id: string | null
  pointsEnabled: boolean
  pointValue: number | null
  currency: string | null
  notes: string | null
  note: string
}

export interface AnnouncementView {
  id: string
  legalEntityId: string | null
  title: string
  body: string
  audienceKind: string
  audienceRef: string | null
  /** The audience as it was when this was published. */
  audienceCount: number | null
  publishedAt: string | null
  expiresOn: string | null
  pinned: boolean
  status: string
  readCount: number
  reachNote: string
}

export interface EngagementDashboardView {
  openSurveys: number
  invitations: number
  responses: number
  /** Null when nothing has been sent — not zero participation. */
  participationPercent: number | null
  recognitionsThisMonth: number
  announcementsLive: number
  announcementReachPercent: number | null
  note: string
}
